import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { SearchableSelect } from '../ui/SearchableSelect';
import {
  cloneBeamPlanFromOrder,
  designFeederCount,
  formatOrderCode,
} from './orderFormHelpers';

/** Orders shown in the picker at once; type in "Search order" to find older ones. */
const CLONE_ORDER_LIMIT = 100;
/** Wait this long after typing before asking the server. */
const SEARCH_DEBOUNCE_MS = 250;

function designOptionLabel(design) {
  if (!design) return '';
  const sample = String(design.sampleNo || '').trim();
  const concept = String(design.conceptName || '').trim();
  if (sample && concept) return `${sample} — ${concept}`;
  return sample || concept || String(design.id);
}

/**
 * Clone beam & feeder colour plan from an existing order.
 * Design filter only lists designs with the same feeder count as the current order.
 * Order list is light (no beamPlan); full plan is loaded when an order is selected.
 */
export function BeamPlanCloneModal({
  open,
  onClose,
  authFetch,
  targetDesignId,
  designs,
  excludeOrderId,
  onApply,
  toast,
}) {
  const [loading, setLoading] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [applying, setApplying] = useState(false);
  /** Matching orders from GET /api/orders (filtered + newest first on the server). */
  const [orders, setOrders] = useState([]);
  const [hasMoreOrders, setHasMoreOrders] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const requestSeqRef = useRef(0);
  const [designFilterId, setDesignFilterId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [sourceOrderFull, setSourceOrderFull] = useState(null);
  const [error, setError] = useState('');

  const targetDesign = useMemo(
    () => (designs || []).find((d) => String(d.id) === String(targetDesignId || '')) || null,
    [designs, targetDesignId]
  );

  const targetFeederCount = designFeederCount(targetDesign);

  /** Designs with the same feeder count as the current order (clone-compatible). */
  const compatibleDesigns = useMemo(() => {
    if (!targetDesign || targetFeederCount <= 0) return [];
    return (designs || []).filter((d) => designFeederCount(d) === targetFeederCount);
  }, [designs, targetDesign, targetFeederCount]);

  const byCreatedAtDesc = (a, b) => {
    const ta = new Date(a?.createdAt || 0).getTime();
    const tb = new Date(b?.createdAt || 0).getTime();
    return tb - ta;
  };

  const designOptions = useMemo(() => {
    const list = compatibleDesigns.slice().sort(byCreatedAtDesc);
    return list.map((d) => ({
      value: String(d.id),
      label: designOptionLabel(d),
    }));
  }, [compatibleDesigns]);

  /**
   * The server applies the same rules the picker used before: designs with the same feeder
   * count as this order, optional design filter, search on order code / id / design no.,
   * excluding the current order.
   */
  const filteredOrders = orders;

  const orderOptions = useMemo(
    () =>
      filteredOrders.map((o) => ({
        value: String(o.id),
        label: formatOrderCode(o.orderCode || o.id),
      })),
    [filteredOrders]
  );

  const resolvedListOrder = useMemo(
    () => filteredOrders.find((o) => String(o.id) === String(selectedId)) || null,
    [filteredOrders, selectedId]
  );

  const clonePreview = useMemo(() => {
    if (!sourceOrderFull || !targetDesignId) return null;
    if (String(sourceOrderFull.id) !== String(selectedId)) return null;
    return cloneBeamPlanFromOrder(sourceOrderFull, targetDesignId, designs);
  }, [sourceOrderFull, selectedId, targetDesignId, designs]);

  useEffect(() => {
    if (!open) return;
    setDesignFilterId(targetDesignId ? String(targetDesignId) : '');
    setSearchQuery('');
    setDebouncedQuery('');
    setSelectedId('');
    setSourceOrderFull(null);
    setError('');
  }, [open, targetDesignId]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchQuery.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    if (!open || !authFetch) return undefined;
    const seq = ++requestSeqRef.current;
    if (targetFeederCount <= 0) {
      setOrders([]);
      setHasMoreOrders(false);
      return undefined;
    }
    const params = new URLSearchParams({
      designFeederCount: String(targetFeederCount),
      size: String(CLONE_ORDER_LIMIT),
    });
    if (excludeOrderId) params.set('excludeId', String(excludeOrderId));
    if (String(designFilterId || '').trim()) params.set('designId', String(designFilterId));
    if (debouncedQuery) params.set('codeOrDesign', debouncedQuery);
    setLoading(true);
    (async () => {
      try {
        const data = await authFetch(`/api/orders?${params.toString()}`);
        if (seq !== requestSeqRef.current) return;
        setOrders(Array.isArray(data?.items) ? data.items : []);
        setHasMoreOrders(Boolean(data?.nextCursor));
      } catch (e) {
        if (seq !== requestSeqRef.current) return;
        setError(e.message || 'Unable to load orders');
        setOrders([]);
        setHasMoreOrders(false);
      } finally {
        if (seq === requestSeqRef.current) setLoading(false);
      }
    })();
    return undefined;
  }, [open, authFetch, targetFeederCount, excludeOrderId, designFilterId, debouncedQuery]);

  useEffect(() => {
    if (!selectedId) return;
    if (!filteredOrders.some((o) => String(o.id) === String(selectedId))) {
      setSelectedId('');
      setSourceOrderFull(null);
    }
  }, [filteredOrders, selectedId]);

  /** Smart fetch: load full beamPlan only for the selected source order. */
  useEffect(() => {
    if (!open || !selectedId || !authFetch) {
      return undefined;
    }
    let cancelled = false;
    setLoadingPlan(true);
    setError('');
    setSourceOrderFull(null);
    (async () => {
      try {
        const data = await authFetch(`/api/orders/${selectedId}`);
        if (cancelled) return;
        setSourceOrderFull(data || null);
      } catch (e) {
        if (cancelled) return;
        setSourceOrderFull(null);
        setError(e.message || 'Unable to load order beam plan');
      } finally {
        if (!cancelled) setLoadingPlan(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, selectedId, authFetch]);

  const handleApply = async () => {
    if (!resolvedListOrder) {
      setError('Select an order to clone from.');
      return;
    }
    if (!sourceOrderFull || String(sourceOrderFull.id) !== String(selectedId)) {
      setError(loadingPlan ? 'Loading beam plan…' : 'Beam plan not loaded yet. Try again.');
      return;
    }
    const result = cloneBeamPlanFromOrder(sourceOrderFull, targetDesignId, designs);
    if (result.error) {
      setError(result.error);
      return;
    }
    setApplying(true);
    setError('');
    try {
      onApply?.(result.beamPlan, sourceOrderFull);
      toast?.success?.(
        `Beam & feeder colours copied from ${formatOrderCode(
          sourceOrderFull.orderCode || sourceOrderFull.id
        )}.`
      );
      onClose?.();
    } finally {
      setApplying(false);
    }
  };

  const validationError = clonePreview?.error && sourceOrderFull ? clonePreview.error : '';
  const canClone = Boolean(
    !loadingPlan && clonePreview?.beamPlan && !clonePreview?.error && sourceOrderFull
  );

  const designHint =
    targetFeederCount > 0
      ? `Only designs with ${targetFeederCount} feeder${
          targetFeederCount === 1 ? '' : 's'
        } (same as this order). Defaults to this order’s design.`
      : 'Select a design on this order first.';

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title="Clone from order"
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose} disabled={applying}>
            Cancel
          </Button>
          <Button
            type="button"
            loading={applying || loadingPlan}
            disabled={applying || loadingPlan || !canClone}
            onClick={handleApply}
          >
            Clone
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error ? (
          <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>
        ) : null}
        {validationError && !error ? (
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {validationError}
          </p>
        ) : null}

        <SearchableSelect
          label="Design"
          value={designFilterId}
          onChange={(id) => {
            setDesignFilterId(id);
            setSelectedId('');
            setSourceOrderFull(null);
            setError('');
          }}
          disabled={loading || designOptions.length === 0}
          placeholder={
            loading
              ? 'Loading…'
              : designOptions.length === 0
                ? targetFeederCount > 0
                  ? 'No designs with matching feeder count'
                  : 'No designs available'
                : 'All matching designs'
          }
          emptyMessage="No design starts with that text"
          options={designOptions}
          hint={designHint}
        />

        <SearchableSelect
          label="Order"
          value={selectedId}
          onChange={(id) => {
            setSelectedId(id);
            setSourceOrderFull(null);
            setError('');
          }}
          disabled={loading}
          placeholder={
            loading
              ? 'Loading orders…'
              : filteredOrders.length === 0
                ? searchQuery.trim() || designFilterId
                  ? 'No matching orders'
                  : 'Select order'
                : 'Select order'
          }
          emptyMessage="No order starts with that text"
          options={orderOptions}
        />

        <Input
          label="Search order"
          placeholder="Order ID or code"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setError('');
          }}
          hint={
            hasMoreOrders
              ? `Showing the newest ${CLONE_ORDER_LIMIT} matching orders. Type an order code or design no. to find older ones.`
              : undefined
          }
        />

        {loadingPlan && selectedId ? (
          <p className="text-sm text-slate-500">Loading beam &amp; feeder colours…</p>
        ) : null}
      </div>
    </Modal>
  );
}
